import filter from 'lodash/filter';
import map from 'lodash/map';
import take from 'lodash/take';
import getUrl from '@/routes/routes/helpers/getUrl';
import { stringifyQS } from '../../../utils';
import { getSearchParamsFromLocation } from '../../../views/CategoryPage/provider/location';

export const seeAllUrl = (countryCode, query = '') =>
  `/${countryCode}/all-events${
    query ? `?name=${encodeURIComponent(query)}` : ''
  }`;

export const nearMeUrl = ({
  countryCode,
  latitude,
  longitude,
  locationName,
  searchParams,
  tagPath,
}) => {
  const point = {
    lat: latitude,
    lng: longitude,
    radius: 50,
  };
  const locationParams = getSearchParamsFromLocation({
    name: locationName || 'Near Me',
    point,
  });
  const querystring = stringifyQS({ ...searchParams, ...locationParams });

  let path = tagPath;
  if (!path) {
    path = `${countryCode?.toLowerCase()}/all-events`;
  }

  return {
    url: `/${path}?${querystring}`,
    path: `/${path}`,
    search: `?${querystring}`,
  };
};

export const formatEvents = (rawEventList) => {
  const getLocation = (event) => {
    const city = event?.place?.address?.city;
    const country = event?.place?.address?.countryLong;

    if (city && country) {
      return `${city}, ${country}`;
    }

    return city || country;
  };
  const processEvent = (event) => {
    const isVirtual = event?.virtualEventDetails?.isVirtual ?? false;
    return {
      location: getLocation(event),
      title: event.title,
      path: getUrl.eventPage(
        event?.place?.address?.countryShort,
        event.slug,
        event.id,
      ),
      image: event?.images?.hero?.medium,
      eventId: event.id,
      isVirtual,
    };
  };
  return {
    total: rawEventList.total,
    events: map(rawEventList.events, processEvent),
  };
};

export const formatTags = ({ tags: rawTagList, searchParams }) => {
  const querystring = stringifyQS({ ...searchParams });

  const processTag = (tag) => {
    const [distanceLabel] = filter(tag.labels, { type: 'Distance' });
    const [locationLabel] = filter(tag.labels, { type: 'Location' });
    const { value: distance } = distanceLabel || {};
    const { value: location } = locationLabel || {};

    return {
      path: `/${tag.path}?${querystring}`,
      title: `${distance}${location ? ` in ${location}` : ''}`,
    };
  };

  return take(map(rawTagList.results, processTag), 5);
};

export const formatDefaultTags = ({ tags, searchParams }) => {
  const querystring = stringifyQS({ ...searchParams });

  const processTag = (tag) => {
    return {
      path: `${tag.path}?${querystring}`,
      title: tag.title,
    };
  };

  return tags.map(processTag);
};

const locationOrdering = [
  'postal_code',
  'locality',
  'administrative_area_level_1',
];

export const formatLocations = ({
  locations,
  path: propPath,
  searchParams,
}) => {
  const processLocation = (location) => {
    const countryCode = location.detail.address_components.find(
      (n) => n.types.indexOf('country') !== -1,
    )?.short_name;

    const locationName = location.detail.address_components.find((n) => {
      const type = locationOrdering.find((lo) => n.types.indexOf(lo) !== -1);
      return !!type;
    })?.long_name;

    const viewport = {
      ne: {
        lat: location.detail.viewport.northEast.latitude,
        lng: location.detail.viewport.northEast.longitude,
      },
      sw: {
        lat: location.detail.viewport.southWest.latitude,
        lng: location.detail.viewport.southWest.longitude,
      },
    };

    const locationParams = getSearchParamsFromLocation({
      name: locationName || location.description,
      viewport,
    });

    const querystring = stringifyQS({ ...searchParams, ...locationParams });

    let path = propPath;
    if (!path) {
      path = `${countryCode?.toLowerCase()}/all-events`;
    }

    // TODO:(tomo@) We're assuming the user won't pick a location
    //              outside their current country as of right now.
    //              This is getting changed in the next set of work
    const permalink = `/${path}?${querystring}`;

    return { name: location.description, permalink };
  };

  return map(locations, processLocation);
};
